import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import Stats from './components/Stats';
import Roster from './components/Roster';
import CTA from './components/CTA';
import Partners from './components/Partners';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import About from './components/About';
import Submission from './components/Submission';
import Gallery from './components/Gallery';
import ToursPage from './components/ToursPage';
import Particles from './components/Particles';
import { Star } from 'lucide-react';

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [loading, setLoading] = useState(true);

  // Initial loading simulation
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Ensure scroll to top happens whenever the page changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  const navigateTo = (page: string) => {
    // If contact is clicked, scroll to footer instead of navigating
    if (page === 'contact') {
      const footer = document.querySelector('footer');
      if (footer) {
        footer.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }
      return;
    }

    // Show a quick loading state when navigating for the "tech" feel
    setLoading(true);
    setTimeout(() => {
        setCurrentPage(page);
        setLoading(false);
    }, 800);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background-dark overflow-hidden">
        <div className="relative flex flex-col items-center">
            {/* Spinning Star Loader */}
            <div className="relative w-24 h-24 mb-4">
                <div className="absolute inset-0 border-2 border-primary/20 rounded-full animate-ping"></div>
                <div className="absolute inset-0 flex items-center justify-center text-primary animate-[spin_3s_linear_infinite]">
                    <Star size={56} />
                </div>
            </div>
            {/* Simplified Loading Text */}
            <div className="text-center">
                <h2 className="text-primary font-mono font-bold text-[10px] tracking-[0.5em] uppercase animate-pulse">
                    loading
                </h2>
            </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-background-light dark:bg-background-dark text-slate-900 dark:text-slate-100 font-display transition-colors duration-300 overflow-x-hidden relative">
      <Particles />
      <Header onNavigate={navigateTo} currentPage={currentPage} />
      
      <main className="relative pt-24 pb-0 grid-bg min-h-screen flex flex-col z-10">
        {/* Animated Scanline Effect */}
        <div className="scanline pointer-events-none absolute w-full h-[100px] z-10 bg-gradient-to-b from-transparent via-primary/5 to-transparent"></div>
        
        {/* Hero Section - Full Width for Home Page */}
        {currentPage === 'home' && <Hero onNavigate={navigateTo} />}
        
        <div className="max-w-7xl mx-auto w-full flex-grow">
            {currentPage === 'home' && (
              <>
                <Stats />
                <Roster />
                <CTA onNavigate={navigateTo} />
                <Partners />
                <FAQ />
              </>
            )}
            
            {currentPage === 'tours' && (
              <ToursPage onNavigate={navigateTo} />
            )}

            {currentPage === 'about' && (
              <About onNavigate={navigateTo} />
            )}

            {currentPage === 'submission' && (
              <Submission onNavigate={navigateTo} />
            )}

            {currentPage === 'gallery' && (
              <Gallery onNavigate={navigateTo} />
            )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default App;